import {
	Checkbox,
	ComboBox,
	DeprecatedCreativeModal as CreativeModal,
	DeprecatedScrollbar as Scrollbar,
	FlexColumn,
	FlexRow,
	SearchField,
	SubHeading,
	Text,
} from '@forecast-it/design-system';
import React, {useEffect, useState, useMemo} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import styled from 'styled-components/macro';
import {useIntl} from 'react-intl';
import {CheckedState} from '@radix-ui/react-checkbox';
import {SageIntacctExportTasksModal_viewer} from './__generated__/SageIntacctExportTasksModal_viewer.graphql';
import {hideLoader, showLoader} from '../../global_loader';
import Util from '../../../forecast-app/shared/util/util';
import CreateSageIntacctTasksMutation from '../../../mutations/CreateSageIntacctTasksMutation';
import DirectApi from '../../../directApi';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import {HorizontalBar} from './SageIntacctModal.styled';

type ListItemType = {
	hasBorderBottom?: boolean;
};

const scrollSectionHeight = window?.innerHeight < 1000 ? '250px' : '500px';
const ListItem = styled.div<ListItemType>`
	width: 550px;
	padding: 18px 8px 18px 8px;
	border-bottom: ${({hasBorderBottom}) => (hasBorderBottom ? '1px solid #ccc' : 'none')};
`;

const ListWrapper = styled.div`
	height: ${scrollSectionHeight};
`;

type SageIntacctExportTasksModalProps = {
	closeModal: () => void;
	viewer: SageIntacctExportTasksModal_viewer;
	isPartOfProjectSyncFlow: boolean;
};

type SageIntacctItem = {
	ITEMID: string;
	NAME: string;
	RECORDNO: string;
};

type ForecastTask = {
	id: string;
	name: string;
};

const SageIntacctExportTasksModal = ({
	closeModal,
	viewer,
	isPartOfProjectSyncFlow,
}: SageIntacctExportTasksModalProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const [tasks, setTasks] = useState<ForecastTask[]>([]);
	const [filteredTasks, setFilteredTasks] = useState<ForecastTask[]>([]);
	const [selectedTasks, setSelectedTasks] = useState<string[]>([]);

	const selectAll = useMemo<boolean>(() => {
		if (filteredTasks.length === 0 || selectedTasks.length === 0) return false;

		return filteredTasks.every(t => selectedTasks.includes(t.id));
	}, [filteredTasks, selectedTasks]);

	const [items, setItems] = useState<SageIntacctItem[]>([]);
	const [selectedItem, setSelectedItem] = useState<string | null>(null);
	const [fetchingItems, setFetchingItems] = useState(false);
	const [exporting, setExporting] = useState<CheckedState>(false);
	const [failed, setFailed] = useState<CheckedState>(false);
	const disableExportButton = !selectedItem || fetchingItems || exporting || selectedTasks.length === 0;

	const closeModalWithToast = () => {
		if (isPartOfProjectSyncFlow) {
			createToast({
				duration: 5000,
				message: `${viewer.project?.name} synced with Sage Intacct`,
			});
		}
		closeModal();
	};

	useEffect(() => {
		if (viewer.project?.tasks?.edges && Array.isArray(viewer.project.tasks.edges)) {
			const projectTasks = viewer.project.tasks.edges.map(edge => edge && edge.node).filter(task => !task.sageIntacctId);
			// Close modal if there are no tasks to list and this was as part of the sync flow
			if (projectTasks.length === 0 && isPartOfProjectSyncFlow) {
				closeModalWithToast();
			} else {
				setTasks(projectTasks);
				setFilteredTasks(projectTasks);
			}
		}
	}, []);

	useEffect(() => {
		setFetchingItems(true);
		DirectApi.Fetch('sageIntacct/items').then(res => {
			if (res?.items) {
				setItems(res.items);
				setFetchingItems(false);
			} else if (res?.error || res?.status === 500 || res?.statusCode === 500) {
				setFailed(true);
				setFetchingItems(false);
			} else {
				setItems([]);
				setFetchingItems(false);
			}
		});
	}, []);

	const selectAllClick = () => {
		if (filteredTasks.length === 0) return;

		if (selectedTasks.length === 0) {
			setSelectedTasks(filteredTasks.map(t => t.id));
		} else {
			const allFilteredIds = filteredTasks.map(t => t.id);
			const isAllFilteredIdsInSelected = allFilteredIds.every(id => selectedTasks.includes(id));

			if (isAllFilteredIdsInSelected) {
				const removedSelectedIds = selectedTasks.filter(id => !allFilteredIds.includes(id));
				setSelectedTasks(removedSelectedIds);
			} else {
				const addedNewSelectedIds = selectedTasks.concat(allFilteredIds);
				setSelectedTasks(addedNewSelectedIds);
			}
		}
	};

	const taskClick = (inputId: string) => {
		if (selectedTasks.includes(inputId)) {
			const tempTasks = selectedTasks.filter(id => id !== inputId);
			setSelectedTasks(tempTasks);
		} else {
			const tempTasks = [...selectedTasks];
			tempTasks.push(inputId);
			setSelectedTasks(tempTasks);
		}
	};

	const onExportClick = () => {
		const sageProjectId = viewer.project?.sageProject?.sageProjectId;
		const projectId = viewer.project?.id;

		setExporting(true);
		showLoader();
		Util.CommitMutation(
			CreateSageIntacctTasksMutation,
			{
				sageIntacctProjectId: sageProjectId,
				sageIntacctItemId: selectedItem,
				forecastProjectId: projectId,
				taskIds: selectedTasks,
			},
			() => {
				setExporting(false);
				hideLoader();
				closeModalWithToast();
			}
		);
	};

	const onFilterChange = (input: string) => {
		setFilteredTasks(tasks.filter(task => task.name.toLowerCase().includes(input.toLowerCase())));
	};

	return (
		<CreativeModal
			title={'Export Tasks to Sage Intacct'}
			size={'l'}
			closeModal={closeModalWithToast}
			creativeButtonText={`Export Tasks to Sage Intacct (${selectedTasks.length})`}
			cancelButtonText={
				isPartOfProjectSyncFlow ? formatMessage({id: 'common.skip'}) : formatMessage({id: 'common.cancel'})
			}
			onCreateHandler={onExportClick}
			onCancelHandler={closeModalWithToast}
			disabled={disableExportButton}
			replicateDesignSystem
		>
			<ComboBox
				label="Item"
				data-cy="combobox-items"
				defaultItems={items}
				value={selectedItem}
				onChange={setSelectedItem}
				width={ComboBox.WIDTH.FLEX}
				disabled={fetchingItems}
			>
				{(item: SageIntacctItem) => (
					<ComboBox.Item key={item.ITEMID} textValue={item.NAME}>
						<Text>{item.NAME}</Text>
					</ComboBox.Item>
				)}
			</ComboBox>

			{failed && <Text color={'error'}>Something has gone wrong, try again or contact support.</Text>}

			<FlexColumn gap={'xs'}>
				<SearchField isExpandable={false} onChange={onFilterChange} width="600px" />
			</FlexColumn>

			<ListItem>
				<FlexRow gap={'xl'}>
					<Checkbox onChange={selectAllClick} checked={selectAll} />
					<SubHeading size={'2'}>{'Select all'}</SubHeading>
				</FlexRow>
			</ListItem>
			<ListWrapper>
				<Scrollbar maxHeight={scrollSectionHeight}>
					<FlexColumn alignItems="flex-start" justifyContent="flex-start">
						{filteredTasks &&
							filteredTasks.map(task => {
								return (
									<ListItem key={'task-' + task.id} hasBorderBottom>
										<FlexRow gap={'xl'}>
											<Checkbox
												onChange={() => taskClick(task.id)}
												checked={selectedTasks.includes(task.id)}
											/>
											<Text size={'2'}>{task.name}</Text>
										</FlexRow>
									</ListItem>
								);
							})}
					</FlexColumn>
				</Scrollbar>
			</ListWrapper>
			<HorizontalBar />
		</CreativeModal>
	);
};

const sageIntacctExportTasksModalQuery = graphql`
	query SageIntacctExportTasksModal_Query($projectId: ID!) {
		viewer {
			actualPersonId
			component(name: "sage_intacct_export_tasks_modal")
			...SageIntacctExportTasksModal_viewer @arguments(projectId: $projectId)
		}
	}
`;

export {sageIntacctExportTasksModalQuery};

export default createFragmentContainer(SageIntacctExportTasksModal, {
	viewer: graphql`
		fragment SageIntacctExportTasksModal_viewer on Viewer @argumentDefinitions(projectId: {type: "ID!"}) {
			project(internalId: $projectId) {
				id
				name
				sageProject {
					sageProjectId
				}
				tasks(first: 10000) @connection(key: "Project_tasks") {
					edges {
						node {
							id
							name
							sageIntacctId
						}
					}
				}
			}
		}
	`,
});
